// Single fetch() helper with retry on transient network failures.
// Retry logic is HTTP-aware (5xx response code, not error-message text).
//
// Auth model: SWA Standard with linked-backend.
// - Calls use relative paths ('/api/billing/records'); SWA proxies to App
//   Service and forwards a signed x-ms-client-principal header.
// - `credentials: 'include'` ensures the SWA session cookie travels with
//   each request (same-origin in production).
// - 401 → redirect to SWA Entra login ONCE (sessionStorage guard breaks
//   any redirect loop after sign-in).
// - 403 → propagate as an HTTP error so the caller can surface a
//   "Forbidden" UI instead of bouncing through login.
import { API_BASE } from '../constants/env.js';

const DEFAULT_RETRIES = 2;
const RETRY_BASE_DELAY_MS = 1000;
const REDIRECT_GUARD_KEY = '_psc_auth_redirect_pending';

async function delay(ms) {
  return new Promise((r) => setTimeout(r, ms));
}

function _redirectToLoginOnce() {
  if (typeof window === 'undefined') return;
  // Guard against repeated redirects in the same tab session. If the
  // user already came back from /.auth/login/aad and we still get 401,
  // something else is wrong — show the error instead of looping.
  try {
    if (window.sessionStorage.getItem(REDIRECT_GUARD_KEY)) return;
    window.sessionStorage.setItem(REDIRECT_GUARD_KEY, '1');
  } catch { /* sessionStorage may be disabled — fall through */ }

  const target = window.location.pathname + (window.location.search || '');
  const url = `/.auth/login/aad?post_login_redirect_uri=${encodeURIComponent(target || '/')}`;
  window.location.replace(url);
}

// Call from the AuthProvider once it has confirmed an authenticated
// principal — clears the one-shot guard so a later session expiry can
// redirect again.
export function clearLoginRedirectGuard() {
  if (typeof window === 'undefined') return;
  try { window.sessionStorage.removeItem(REDIRECT_GUARD_KEY); } catch {}
}

// A response that is a FILE, not JSON — the CSV export endpoint.
//
// Deliberately NOT retried: an export streams for as long as it takes to walk
// the whole filtered set, and silently re-issuing that after a hiccup means two
// expensive queries and a second audit row for one user click. A visible error
// they can retry themselves is the honest behaviour.
export async function requestFile(path, options = {}) {
  const res = await fetch(`${API_BASE}${path}`, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
    ...options,
  });

  if (res.status === 401) {
    _redirectToLoginOnce();
    const authErr = new Error('401: redirecting to sign-in');
    authErr.status = 401;
    throw authErr;
  }

  if (!res.ok) {
    // The backend refuses an over-cap export with a JSON {detail} explaining
    // how to narrow it — surface that sentence rather than "500".
    const text = await res.text().catch(() => '');
    let message = text;
    try { message = JSON.parse(text)?.detail || text; } catch { /* not JSON */ }
    const httpErr = new Error(message || `Request failed (${res.status})`);
    httpErr.status = res.status;
    throw httpErr;
  }

  return {
    blob: await res.blob(),
    filename: filenameFromDisposition(res.headers.get('Content-Disposition')),
  };
}

function filenameFromDisposition(header) {
  if (!header) return null;
  const match = /filename="?([^";]+)"?/i.exec(header);
  return match ? match[1] : null;
}

export async function request(path, options = {}, retries = DEFAULT_RETRIES) {
  let lastErr;
  for (let attempt = 0; attempt <= retries; attempt++) {
    let res;
    try {
      res = await fetch(`${API_BASE}${path}`, {
        credentials: 'include',
        headers: { 'Content-Type': 'application/json', ...(options.headers || {}) },
        ...options,
      });
    } catch (err) {
      // Network-level failure — back off and retry.
      lastErr = err;
      if (attempt < retries) {
        await delay(RETRY_BASE_DELAY_MS * (attempt + 1));
        continue;
      }
      throw err;
    }

    if (res.ok) {
      return res.json();
    }

    // 401: not signed in → redirect to login once. The guard prevents
    // the post-login callback from bouncing back into login.
    if (res.status === 401) {
      _redirectToLoginOnce();
      const authErr = new Error('401: redirecting to sign-in');
      authErr.status = 401;
      throw authErr;
    }

    const text = await res.text().catch(() => '');
    const httpErr = new Error(`${res.status}: ${text}`);
    httpErr.status = res.status;
    // Attach the parsed body so callers can read structured error info
    // (e.g. PipelineBusyError → 409 with {detail, conflict: {...}}).
    try { httpErr.body = text ? JSON.parse(text) : null; } catch { httpErr.body = null; }

    if (res.status >= 500 && res.status < 600 && attempt < retries) {
      lastErr = httpErr;
      await delay(RETRY_BASE_DELAY_MS * (attempt + 1));
      continue;
    }
    throw httpErr;
  }
  // Defensive — exhausted retries without throwing.
  throw lastErr || new Error('request failed');
}
